class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const searchOverlay = document.getElementById('SearchOverlay');
    if (searchOverlay) {
      searchOverlay.open(this.input);
      return;
    }

    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

class DrawerSearch extends SearchForm {
  constructor() {
    super();

    this.headerSearchInputs = document.querySelectorAll('.header__search input[type="search"]');
    this.allSearchInputs = this.getAllSyncInputs();
    this.transferDelayMs = 500;
    this.transferTimeout = null;
    this.transferAnnouncement = this.getAttribute('data-transfer-announcement') || '';
    this.statusElement = this.querySelector('.menu-drawer__search-status');
    this.setupEventListeners();
    this.bindDrawerToggleReset();
  }

  announceStatus(message) {
    if (!this.statusElement || !message) return;

    this.statusElement.textContent = '';
    requestAnimationFrame(() => {
      this.statusElement.textContent = message;
    });
  }

  clearTransferTimeout() {
    if (this.transferTimeout) {
      window.clearTimeout(this.transferTimeout);
      this.transferTimeout = null;
    }
  }

  clearDrawerSearch() {
    if (this.input) this.input.value = '';
  }

  clearHeaderSearch() {
    this.headerSearchInputs.forEach((input) => {
      input.value = '';
      this.toggleHeaderResetButton(input);
    });

    document.querySelectorAll('.header__search predictive-search').forEach((predictiveSearch) => {
      predictiveSearch.close(true);
    });

    document.querySelectorAll('.header__search details-modal').forEach((modal) => {
      if (modal.isOpen?.()) modal.close(false);
    });
  }

  cancelPendingTransfer() {
    const hadPendingTransfer = this.transferTimeout !== null;
    this.clearTransferTimeout();
    if (hadPendingTransfer) {
      this.clearDrawerSearch();
      this.clearHeaderSearch();
      if (this.statusElement) this.statusElement.textContent = '';
    }
  }

  bindDrawerToggleReset() {
    const details = document.getElementById('Details-menu-drawer-container');
    details?.addEventListener('toggle', () => {
      if (details.open) {
        this.clearTransferTimeout();
        if (this.statusElement) this.statusElement.textContent = '';
      } else {
        this.cancelPendingTransfer();
      }
    });
  }

  getTemplateSearchInput() {
    return document.querySelector('main-search input[type="search"]');
  }

  getTemplatePredictiveSearch() {
    return this.getTemplateSearchInput()?.closest('predictive-search');
  }

  closeTemplatePredictiveSearch() {
    this.getTemplatePredictiveSearch()?.close();
  }

  getAllSyncInputs() {
    const inputs = [];
    if (this.input) inputs.push(this.input);
    this.headerSearchInputs.forEach((input) => {
      if (!inputs.includes(input)) inputs.push(input);
    });
    const templateInput = this.getTemplateSearchInput();
    if (templateInput && !inputs.includes(templateInput)) inputs.push(templateInput);
    return inputs;
  }

  setupEventListeners() {
    const allSearchForms = [];
    this.allSearchInputs.forEach((input) => {
      if (input.form) allSearchForms.push(input.form);
    });

    if (this.input) {
      this.boundActivateFromDrawer = this.onDrawerSearchActivate.bind(this);
      this.input.addEventListener('input', this.onDrawerInput.bind(this));
      this.input.addEventListener('click', this.boundActivateFromDrawer);
      this.input.addEventListener('focus', this.boundActivateFromDrawer);
      this.input.form?.addEventListener('submit', this.onDrawerFormSubmit.bind(this));

      // Search icon / field chrome should open the overlay the same way as focusing the input.
      this.querySelector('.menu-drawer__search-button')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.onDrawerSearchActivate(event);
      });
    }

    if (this.allSearchInputs.length < 2) return;

    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.headerSearchInputs.forEach((input) => input.addEventListener('input', this.onHeaderInput.bind(this)));
  }

  onDrawerSearchActivate(event) {
    if (!this.isDrawerOpen()) return;

    const searchOverlay = document.getElementById('SearchOverlay');
    if (searchOverlay?.hasAttribute('open')) {
      event?.preventDefault?.();
      searchOverlay.focusOverlayInput(searchOverlay.overlayInput?.value || '');
      return;
    }

    event?.preventDefault?.();
    this.openSearchOverlayFromDrawer(this.input?.value?.trim() || '');
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onDrawerFormSubmit(event) {
    if (!this.input?.value.trim()) {
      event.preventDefault();
    }
  }

  onDrawerInput(event) {
    if (!this.isDrawerOpen() && document.activeElement !== this.input) return;

    const value = event.target.value;
    this.clearTransferTimeout();

    if (!value.length) return;

    this.transferTimeout = window.setTimeout(() => {
      this.transferTimeout = null;
      this.transferToHeaderSearch(value);
    }, this.transferDelayMs);
  }

  onHeaderInput(event) {
    this.keepInSync(event.target.value, event.target);
  }

  isDrawerOpen() {
    const details = document.getElementById('Details-menu-drawer-container');
    return details?.hasAttribute('open');
  }

  closeDrawer({ restoreFocus = true } = {}) {
    const headerDrawer = document.querySelector('header-drawer');
    const details = document.getElementById('Details-menu-drawer-container');
    if (!details?.hasAttribute('open')) return;

    const summary = details.querySelector('summary');
    const elementToFocus = restoreFocus ? summary : false;

    if (headerDrawer && summary) {
      headerDrawer.closeMenuDrawer(new Event('input'), elementToFocus);
    } else {
      details.removeAttribute('open');
      document.body.classList.forEach((className) => {
        if (className.startsWith('overflow-hidden-')) document.body.classList.remove(className);
      });
    }
  }

  getActiveHeaderSearchInput() {
    for (const input of this.headerSearchInputs) {
      if (input.offsetParent !== null) return input;
    }
    return null;
  }

  openHeaderSearchModal(triggerInput) {
    const searchOverlay = document.getElementById('SearchOverlay');
    if (searchOverlay) {
      searchOverlay.open(triggerInput);
      return;
    }

    const detailsModal = triggerInput?.closest('details-modal');
    if (!detailsModal) return;

    const details = detailsModal.querySelector('details');
    if (!details || details.hasAttribute('open')) return;

    details.setAttribute('open', true);
    document.body.classList.add('overflow-hidden');

    if (typeof trapFocus === 'function') {
      trapFocus(
        details.querySelector('[tabindex="-1"]'),
        triggerInput
      );
    }
  }

  openSearchOverlayFromDrawer(value = '') {
    const searchOverlay = document.getElementById('SearchOverlay');
    if (!searchOverlay) return;

    const trimmedValue = value.trim();
    this.clearTransferTimeout();

    if (this.isDrawerOpen()) this.closeDrawer({ restoreFocus: false });

    this.closeTemplatePredictiveSearch();

    const headerInput = this.getActiveHeaderSearchInput();
    if (headerInput) {
      headerInput.value = trimmedValue;
      this.toggleHeaderResetButton(headerInput);
      this.syncTemplateSearchInput(trimmedValue);
    }

    if (this.input) this.input.value = trimmedValue;

    this.openHeaderSearchModal(headerInput);

    if (trimmedValue.length) {
      this.announceStatus(this.transferAnnouncement);
    }
  }

  toggleHeaderResetButton(headerInput) {
    const resetButton = headerInput.form?.querySelector('button[type="reset"]');
    if (!resetButton) return;

    resetButton.classList.toggle('hidden', headerInput.value.length === 0);
  }

  syncTemplateSearchInput(value) {
    const templateInput = this.getTemplateSearchInput();
    if (!templateInput) return;

    templateInput.value = value;
    this.toggleHeaderResetButton(templateInput);
  }

  transferToHeaderSearch(value) {
    this.openSearchOverlayFromDrawer(value);
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

customElements.define('main-search', MainSearch);
customElements.define('drawer-search', DrawerSearch);
